import { Injectable } from '@angular/core';
import {AngularFireFunctions} from '@angular/fire/functions';
import { EditUserData } from '../interfaces/users';
import firebase from 'firebase';
import 'firebase/auth';
import HttpsError = firebase.functions.HttpsError;
// existing types above
import {UtilService} from './util.service';
import {PaymentMethod} from '../interfaces/shared';

@Injectable({
  providedIn: 'root'
})
export class FireApiService {

  constructor(
    private fireFunctions: AngularFireFunctions,
    private util: UtilService
  ) { }

  async createTicketFromDriverQR(driverId: string, code: string): Promise<{
    rideId: string;
    vendorId: string;
    vendorName: string;
    pickupDriverId: string;
    createDate: number;
  }> {
    const request = {driverId, code};
    const callable = this.fireFunctions.httpsCallable('createTicketFromDriverQR');
    const response: any = await callable(request).toPromise();
    return (response?.data || response) as {
      rideId: string;
      vendorId: string;
      vendorName: string;
      pickupDriverId: string;
      createDate: number;
    };
  }

  async generateWhatsAppTicketLink(rideId: string, driverId: string): Promise<{ url: string }> {
    const request = {rideId, driverId};
    const callable = this.fireFunctions.httpsCallable('generateWhatsAppTicketLink');
    const response: any = await callable(request).toPromise();
    return (response?.data || response) as { url: string };
  }

  async linkAnonymousTicketToUser(rideId: string, oldAnonymousUid: string): Promise<boolean> {
    // Keep legacy key for backward compatibility if backend still expects oldCustomerId.
    const request = {rideId, oldAnonymousUid, oldCustomerId: oldAnonymousUid};
    const callable = this.fireFunctions.httpsCallable('linkAnonymousTicketToUser');
    const response: any = await callable(request).toPromise();
    return (response?.data ?? response) as boolean;
  }

  static handleErrorQuietly(error: HttpsError) {
    console.log(error.message);
  }

  updateUserProfile(request: EditUserData) {
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('editCustomerData');
      callable(request).toPromise().then((response) => {
        console.log(response);
        resolve(true);
      }).catch((error: HttpsError) => {
        FireApiService.handleErrorQuietly(error);
        reject(error.message);
      });
    });
  }
  // Cloud Function: whatsappLink (https.onCall) -> returns { url, code }
  async createWhatsappLink(driverId: string, code: string): Promise<{ url: string; code: string }> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        // onCall requires context.auth; anonymous auth is acceptable if enabled
        // create an anonymous user if none exists
		await firebase.auth().signInAnonymously();
      }
      // Ensure user has at least anonymous sign-in before calling
      const callable = this.fireFunctions.httpsCallable<{ driverId: string, code: string }, { url: string; code: string }>('whatsappLink');
      const response: any = await callable({driverId, code}).toPromise();
      const data = response && response.data ? response.data : response;
      if (!data || typeof data.url !== 'string' || typeof data.code !== 'string') {
        console.warn('[createWhatsappLink] Unexpected response shape', data);
      }
      return data as { url: string; code: string };
    } catch (error: any) {
      console.warn('[createWhatsappLink] callable error', error);
      FireApiService.handleErrorQuietly(error);
      throw error?.message || 'Failed to create WhatsApp link';
    }
  }
  createTicket(driverId: string, code: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const request = {driverId, code};
      const callable = this.fireFunctions.httpsCallable('createRide');
      callable(request).toPromise().then((response) => {
        console.log('Ride ID: ', response);
        resolve(response as string);
      }).catch((error) => {
        FireApiService.handleErrorQuietly(error);
        reject(error.message);
      });
    });
  }

  updateEmailTrigger(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('emailUpdated');
      callable(null).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        FireApiService.handleErrorQuietly(error);
        reject(error.message);
      });
    });
  }

  validateRideCode(rideId: string, shopId: string, code: string): Promise<boolean> {
    const request = {
      rideId,
      shopId,
      code
    };
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('validatePassCustomer');
      callable(request).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        this.handleErrorLoudly(error);
        reject(error.message);
      });
    });
  }

  validateTemporaryPass(rideId: string, code: string) {
    const request = {
      rideId,
      code
    };
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('validateTemporaryPassCustomer');
      callable(request).toPromise().then((response) => {
        resolve(response as number);
      }).catch((error) => {
        this.handleErrorLoudly(error);
        reject(error.message);
      });
    });
  }

  upgradeAccount(editData: EditUserData, email: string, password: string) {
    const request = {
      userEditData: editData,
      email,
      password,
    };
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('updateAccount');
      callable(request).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        this.handleErrorLoudly(error);
        reject(error);
      });
    });
  }

  upgradeAccountWithGoogle(rideId: string, oldCustomerId: string, showErrorToast: boolean = true) {
    const request = {
      rideId,
      oldCustomerId
    };
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('upgradeAccountWithGoogle');
      callable(request).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        if (showErrorToast) {
          this.handleErrorLoudly(error);
        }
        reject(error);
      });
    });
  }

  reconcileAnonymousSessionUpgrade(oldId: string) {
    const request = oldId;
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('reconcileAnonymousSessionUpgrade');
      callable(request).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        this.handleErrorLoudly(error);
        reject(error);
      });
    });
  }

  requestCar(rideId: string, tip: number, paymentMethod: PaymentMethod = null) {
    const request = {
      rideId,
      tip,
      paymentMethod
    };
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('requestMyCar');
      callable(request).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        this.handleErrorLoudly(error);
        reject(error);
      });
    });
  }

  submitReview(rideId: string, stars: number, reviewText: string) {
    const request = {rideId, stars, reviewText};
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('reviewRide');
      callable(request).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        this.handleErrorLoudly(error);
        reject(error);
      });
    });
  }

  addNotificationToken(token: string, deviceData: string) {
    const request = {token, deviceData};
    return new Promise((resolve, reject) => {
      const callable = this.fireFunctions.httpsCallable('saveNotificationToken');
      callable(request).toPromise().then((response) => {
        resolve(response);
      }).catch((error) => {
        console.log(error);
        reject(error);
      });
    });
  }

  private handleErrorLoudly(error: HttpsError) {
    console.log(error);
    this.util.toast(error.message, 'danger', 'bottom');
  }
}
